import { normalizeEmail } from "../../../lib/utils";

const slotStartMs = (slot) => (slot?.start ? new Date(slot.start).getTime() : 0);

/**
 * Order the results-slot list for the session-poll "Results" region.
 *
 * Layer-B privacy: slot ORDER itself leaks vote popularity. When the viewer is
 * NOT entitled to per-slot vote DETAILS (`detailsRevealed === false` — e.g. a
 * non-creator under a restrictive `voteVisibility` while votes are still loaded
 * because identities aren't hidden), we MUST NOT order by the hidden tallies.
 * In that case fall back to a purely chronological order (earliest start first),
 * independent of `sortMode`. Only when details are revealed do the
 * tally-weighted "preferred"/"attendance" comparators apply.
 *
 * @param {object[]} slots - rows shaped `{ ...slot, counts: { feasible, preferred } }`.
 * @param {"preferred"|"attendance"} sortMode
 * @param {boolean} detailsRevealed - the per-slot-details entitlement gate.
 * @returns {object[]} a new, sorted array (input is not mutated).
 */
export const sortResultSlots = ({ slots = [], sortMode = "preferred", detailsRevealed = false }) => {
  const rows = [...slots];
  if (!detailsRevealed) {
    return rows.sort((a, b) => slotStartMs(a) - slotStartMs(b));
  }
  return rows.sort((a, b) => {
    const aCounts = a?.counts || { feasible: 0, preferred: 0 };
    const bCounts = b?.counts || { feasible: 0, preferred: 0 };
    const aTime = slotStartMs(a);
    const bTime = slotStartMs(b);
    if (sortMode === "attendance") {
      if (bCounts.feasible !== aCounts.feasible) {
        return bCounts.feasible - aCounts.feasible;
      }
      if (bCounts.preferred !== aCounts.preferred) {
        return bCounts.preferred - aCounts.preferred;
      }
      return aTime - bTime;
    }
    if (bCounts.preferred !== aCounts.preferred) {
      return bCounts.preferred - aCounts.preferred;
    }
    if (bCounts.feasible !== aCounts.feasible) {
      return bCounts.feasible - aCounts.feasible;
    }
    return aTime - bTime;
  });
};

export const buildAttendanceSetFromVoters = (voters) => {
  const attendanceSet = new Set();
  const feasibleVoters = voters?.feasible || [];
  feasibleVoters.forEach((voter) => {
    const normalized = normalizeEmail(voter?.email);
    if (normalized) {
      attendanceSet.add(normalized);
    }
  });
  return attendanceSet;
};

export const filterSlotsByRequiredAttendance = ({
  slots = [],
  slotVotersById = {},
  requiredEmails = [],
}) => {
  const normalizedRequired = (requiredEmails || [])
    .map((email) => normalizeEmail(email))
    .filter(Boolean);

  if (normalizedRequired.length === 0) {
    return slots;
  }

  return slots.filter((slot) => {
    const voters = slotVotersById?.[slot.id];
    if (!voters) return false;
    const attendanceSet = buildAttendanceSetFromVoters(voters);
    return normalizedRequired.every((email) => attendanceSet.has(email));
  });
};
